import React, { useEffect, useState } from "react";
import {
    Alert, Badge, Button, Card, CardBody, Col, Container, Input, Label, Row, Spinner, Table
} from "reactstrap";

import BreadCrumb from "../../Components/Common/BreadCrumb";
import { ExportAtomAPI } from "api";
import { puoEsportare } from "./ammessi";

const moment = require("moment");

/**
 * ESTRAZIONE DEI DATI VERSO ATOM.
 *
 * ┌─ COSA SUCCEDE QUI, IN UNA RIGA ──────────────────────────────────────────────────────┐
 * │                                                                                      │
 * │ Si sceglie un periodo, si guarda quante righe uscirebbero, si scarica un file Excel.  │
 * │ Quel file si carica su ATOM, che lo legge e propone da solo cosa c'è dentro.          │
 * └──────────────────────────────────────────────────────────────────────────────────────┘
 *
 * 🔴 DAL FILE ESCE L'ANAGRAFICA COMPLETA DEI CORSISTI.
 *
 * Nome, email, telefono, codice fiscale, indirizzo, data di nascita. È il dato più
 * delicato che questa piattaforma ospiti, e una volta scaricato vive fuori: non si
 * richiama indietro e non si sa più dove finisce. Va detto a chi preme il pulsante, non
 * lasciato capire.
 */

/** I trimestri dal 2023 a oggi: sono il taglio che si usa davvero. */
function trimestri() {
    const elenco = [];
    const oggi = moment();

    for (let anno = 2023; anno <= oggi.year(); anno++) {
        for (let t = 1; t <= 4; t++) {
            const dal = moment({year: anno, month: (t - 1) * 3, day: 1});
            if (dal.isAfter(oggi)) break;

            elenco.push({
                etichetta: "T" + t + " " + anno,
                dal: dal.format("YYYY-MM-DD"),
                al: dal.clone().endOf("quarter").format("YYYY-MM-DD")
            });
        }
    }

    return elenco.reverse();
}

const NOMI_FOGLI = {
    cliente: "Clienti",
    lead: "Lead",
    "lead note": "Note dei lead",
    vendita: "Vendite",
    pagamento: "Pagamenti",
    appuntamento: "Appuntamenti"
};

const ExportAtom = () => {
    const [dal, setDal] = useState("");
    const [al, setAl] = useState("");

    const [conteggio, setConteggio] = useState(null);
    const [caricamento, setCaricamento] = useState(false);
    const [scaricamento, setScaricamento] = useState(false);
    const [esito, setEsito] = useState(null);

    document.title = "Estrazione verso ATOM | Cambia La Tua Vita";

    /**
     * ⚠️ La pagina si difende anche da sola, non solo nascondendosi dal menu.
     *
     * All'indirizzo ci si arriva anche scrivendolo, e chi lo fa non è uno che si perde nei
     * menu. Qui è cortesia — un messaggio invece di una schermata rotta; la porta la chiude
     * il backend, che risponde 403 a chiunque non sia nell'elenco.
     */
    const ammesso = puoEsportare();

    const contaRighe = async (a, b) => {
        setCaricamento(true);
        setEsito(null);

        try {
            const j = await ExportAtomAPI.conteggio(a, b);
            setConteggio(j);
        } catch (e) {
            setConteggio(null);
            setEsito({ok: false, messaggio: "Non riesco a contare le righe. Riprova."});
        }

        setCaricamento(false);
    };

    // Al primo ingresso si conta tutto: fa vedere subito la dimensione del problema.
    // ⚠️ Ma solo a chi puo': agli altri il server risponderebbe 403, e si vedrebbe un
    // errore rosso al posto della spiegazione.
    useEffect(() => { if (ammesso) contaRighe("", ""); }, [ammesso]);

    const scegliTrimestre = (t) => {
        setDal(t.dal);
        setAl(t.al);
        contaRighe(t.dal, t.al);
    };

    const scarica = async () => {
        setScaricamento(true);
        setEsito(null);

        try {
            const nome = await ExportAtomAPI.scarica(dal, al);
            setEsito({ok: true, messaggio: "Scaricato: " + nome});
        } catch (e) {
            setEsito({ok: false, messaggio: "L'estrazione non è riuscita. Riprova."});
        }

        setScaricamento(false);
    };

    const totale = conteggio
        ? Object.values(conteggio.conteggio).reduce((s, n) => s + n, 0)
        : 0;

    if (!ammesso) {
        return (
            <div className="page-content">
                <Container fluid>
                    <BreadCrumb title="Estrazione verso ATOM" pageTitle="Amministrazione" />
                    <Alert color="warning">
                        <b>Questa pagina è riservata.</b> Da qui esce l'anagrafica completa dei
                        corsisti, e l'accesso è limitato a poche persone. Se ti serve, chiedi a
                        chi amministra la piattaforma.
                    </Alert>
                </Container>
            </div>
        );
    }

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <BreadCrumb title="Estrazione verso ATOM" pageTitle="Amministrazione" />

                    <Row>
                        <Col lg={12}>
                            <Card>
                                <CardBody>
                                    <h5 className="mb-1">Portare i dati dentro ATOM</h5>
                                    <p className="text-muted mb-3">
                                        Scegli un periodo, guarda quante righe escono, scarica il file.
                                        Poi lo carichi su ATOM, in <b>Impostazioni → Importazione dati</b>:
                                        lì riconosce da solo cosa c'è dentro e ti chiede conferma prima
                                        di scrivere qualunque cosa.
                                    </p>

                                    <Alert color="danger" className="mb-0">
                                        <b>Da questo file esce l'anagrafica completa dei corsisti</b> —
                                        nome, email, telefono, codice fiscale, indirizzo, data di
                                        nascita. Una volta scaricato vive fuori da qui: non si richiama
                                        indietro. Scaricalo solo dove serve, e non lasciarlo in giro.
                                    </Alert>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>

                    <Row>
                        <Col lg={5}>
                            <Card>
                                <CardBody>
                                    <h6 className="mb-3">Il periodo</h6>

                                    <Row className="mb-3">
                                        <Col sm={6}>
                                            <Label className="form-label">Dal</Label>
                                            <Input
                                                type="date" value={dal}
                                                onChange={(e) => setDal(e.target.value)}
                                            />
                                        </Col>
                                        <Col sm={6}>
                                            <Label className="form-label">Al</Label>
                                            <Input
                                                type="date" value={al}
                                                onChange={(e) => setAl(e.target.value)}
                                            />
                                        </Col>
                                    </Row>

                                    <Button
                                        color="light" className="w-100 mb-3"
                                        onClick={() => contaRighe(dal, al)}
                                        disabled={caricamento}
                                    >
                                        {caricamento ? <Spinner size="sm" className="me-1" /> : null}
                                        Conta le righe
                                    </Button>

                                    <div className="text-muted mb-2" style={{fontSize: 13}}>
                                        Oppure prendi un trimestre già pronto:
                                    </div>

                                    <div className="d-flex flex-wrap gap-1">
                                        {trimestri().map((t) => (
                                            <Button
                                                key={t.etichetta} size="sm"
                                                color={dal === t.dal && al === t.al ? "primary" : "light"}
                                                onClick={() => scegliTrimestre(t)}
                                            >
                                                {t.etichetta}
                                            </Button>
                                        ))}
                                        <Button
                                            size="sm"
                                            color={!dal && !al ? "primary" : "light"}
                                            onClick={() => { setDal(""); setAl(""); contaRighe("", ""); }}
                                        >
                                            Tutto
                                        </Button>
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>

                        <Col lg={7}>
                            <Card>
                                <CardBody>
                                    <h6 className="mb-3">
                                        Cosa esce
                                        {dal || al ? (
                                            <Badge color="light" className="text-dark ms-2">
                                                {dal ? moment(dal).format("DD/MM/YYYY") : "dall'inizio"}
                                                {" → "}
                                                {al ? moment(al).format("DD/MM/YYYY") : "a oggi"}
                                            </Badge>
                                        ) : (
                                            <Badge color="light" className="text-dark ms-2">tutto lo storico</Badge>
                                        )}
                                    </h6>

                                    {caricamento ? (
                                        <div className="text-center py-4"><Spinner /></div>
                                    ) : conteggio ? (
                                        <React.Fragment>
                                            <Table className="align-middle mb-3">
                                                <tbody>
                                                    {Object.keys(conteggio.conteggio).map((k) => (
                                                        <tr key={k}>
                                                            <td>{NOMI_FOGLI[k] || k}</td>
                                                            <td className="text-end fw-semibold">
                                                                {conteggio.conteggio[k].toLocaleString("it-IT")}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                                <tfoot className="table-light">
                                                    <tr>
                                                        <th>Totale righe</th>
                                                        <th className="text-end">{totale.toLocaleString("it-IT")}</th>
                                                    </tr>
                                                </tfoot>
                                            </Table>

                                            {/**
                                              * ⚠️ I clienti NON si tagliano sul periodo, e va detto qui.
                                              * Un lead del 2023 può essere diventato cliente nel 2025: se
                                              * uscissero solo i clienti del trimestre, quel collegamento
                                              * si perderebbe, e si ricostruisce solo durante
                                              * l'importazione — mai dopo.
                                              */}
                                            <Alert color="light" className="border">
                                                <b>I clienti escono sempre tutti</b>, anche quando scegli
                                                un trimestre. Un lead vecchio può essere diventato cliente
                                                di recente, e quel collegamento si ricostruisce solo
                                                mentre ATOM importa.
                                            </Alert>

                                            <div className="text-muted" style={{fontSize: 13}}>
                                                Le date nel file sono ora locale <b>{conteggio.fuso}</b>.
                                                C'è un foglio «leggimi» che lo dice, così chi importa non
                                                deve indovinare.
                                            </div>
                                        </React.Fragment>
                                    ) : (
                                        <div className="text-muted py-3">
                                            Premi «conta le righe» per vedere cosa uscirebbe.
                                        </div>
                                    )}
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>

                    {esito ? (
                        <Alert color={esito.ok ? "success" : "danger"}>{esito.messaggio}</Alert>
                    ) : null}

                    <Row>
                        <Col lg={12}>
                            <Card>
                                <CardBody className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                    <div className="text-muted">
                                        {totale > 20000
                                            ? "Sono tante righe: il file pesa parecchio e ci mette qualche decina di secondi. Un trimestre alla volta si controlla meglio."
                                            : "Il file si genera in pochi secondi."}
                                    </div>
                                    <Button
                                        color="primary" onClick={scarica}
                                        disabled={scaricamento || caricamento || !conteggio}
                                    >
                                        {scaricamento
                                            ? <Spinner size="sm" className="me-1" />
                                            : <i className="ri-download-2-line me-1" />}
                                        Scarica il file per ATOM
                                    </Button>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
        </React.Fragment>
    );
};

export default ExportAtom;
